import { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Play, Maximize2, X } from 'lucide-react';
import { instagramReelsService } from '../services/instagram-reels.service';
import { useQuery } from '@tanstack/react-query';

export function InstagramReelsWidget() {
  const [selectedReel, setSelectedReel] = useState<string | null>(null);
  const [fullScreenMode, setFullScreenMode] = useState(false);

  const { data: reels = [], isLoading } = useQuery({
    queryKey: ['instagram-reels'],
    queryFn: () => instagramReelsService.getReels(20),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  useEffect(() => {
    if (selectedReel && reels.length > 0) {
      const script = document.createElement('script');
      script.src = 'https://www.instagram.com/embed.js';
      script.async = true;

      const existingScript = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      if (!existingScript) {
        document.body.appendChild(script);
      }

      const timer = setTimeout(() => {
        if (typeof window !== 'undefined' && (window as any).instgrm) {
          (window as any).instgrm.Embeds.process();
        }
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [selectedReel, reels, fullScreenMode]);

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-8 border-2 border-pink-200 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Diabetes Education from The Sugar Doctor
            </h3>
            <p className="text-gray-600">
              Watch expert advice and tips from @thesugar_doctor
            </p>
          </div>
          <a
            href="https://www.instagram.com/thesugar_doctor/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-md transition-all"
          >
            <span>Follow</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-pink-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading reels...</span>
        </div>
      </div>
    );
  }

  if (reels.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-8 border-2 border-pink-200 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              Diabetes Education from The Sugar Doctor
            </h3>
            <p className="text-gray-600">
              Watch expert advice and tips from @thesugar_doctor
            </p>
          </div>
          <a
            href="https://www.instagram.com/thesugar_doctor/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-md transition-all"
          >
            <span>Follow</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {selectedReel ? (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => setSelectedReel(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
              >
                ← Back to all reels
              </button>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setFullScreenMode(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <Maximize2 className="w-4 h-4" />
                  <span>Full Screen</span>
                </button>
                <a
                  href={selectedReel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Open in Instagram</span>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <blockquote
                className="instagram-media"
                data-instgrm-permalink={selectedReel}
                data-instgrm-version="14"
                style={{
                  background: '#FFF',
                  border: '0',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  margin: '1px',
                  maxWidth: '540px',
                  minWidth: '326px',
                  padding: '0',
                  width: '99%',
                }}
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {reels.map((reel) => (
              <button
                key={reel.id}
                type="button"
                onClick={() => setSelectedReel(reel.reelUrl)}
                className="group relative aspect-[9/16] bg-gradient-to-br from-pink-100 to-orange-100 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-lg transition-all">
                    <Play className="w-8 h-8 text-pink-600 ml-1" fill="currentColor" />
                  </div>
                </div>

                {reel.title && (
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-white text-sm font-semibold">
                    <p className="line-clamp-2">{reel.title}</p>
                  </div>
                )}

                <div className="absolute top-2 right-2">
                  <div className="bg-gradient-to-r from-pink-500 to-orange-500 text-white text-xs px-2 py-1 rounded-full font-semibold">
                    Reel
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
          <p className="text-sm text-green-800">
            <strong>Live Instagram Reels:</strong> Click on any reel to watch it. New reels are automatically added every 24 hours!
          </p>
        </div>
      </div>

      {fullScreenMode && selectedReel && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            onClick={() => setFullScreenMode(false)}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all z-10"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="w-full max-w-2xl">
            <blockquote
              className="instagram-media"
              data-instgrm-permalink={selectedReel}
              data-instgrm-version="14"
              style={{
                background: '#FFF',
                border: '0',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                margin: '0 auto',
                maxWidth: '658px',
                minWidth: '326px',
                padding: '0',
                width: '100%',
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
